import { useTheme } from '@emotion/react';
import { Down, Up } from '@icon-park/react';
import cn from 'classnames';
import { noop } from 'lodash';
import { memo, type ReactNode, useCallback } from 'react';

import { SORT } from '@didaskill/domain/platform-constants';

import { parseSortParameters } from '../../../../utils/arrays';
import { ICON_SIZES } from '../../../general/icon/constants';
import { Typography } from '../../../general/typography/Typography';
import styles from './HeaderCell.styles';

export type HeaderCellProps = {
  /** Key name of the column. */
  name: string;
  /** Title of the Column. */
  title?: ReactNode;
  /** Event that triggers toggling the sort. */
  toggleSort?: (name: string) => void;
  /** Use sortings. */
  sortable?: boolean;
  /** Current sorting direction. */
  sort?: string;
  /** Specify how cell content is aligned (left/right, not specified = left). */
  align?: string;
};

export const HeaderCellBase = ({
  name,
  title = null,
  toggleSort = noop,
  sortable = false,
  sort = null,
  align = null,
}: HeaderCellProps) => {
  const theme = useTheme();
  const onToggleSort = useCallback(() => toggleSort(name), [toggleSort, name]);
  const { sortBy, sortDirection } = parseSortParameters(sort);

  const isSorted = sortBy === name;

  return (
    <div css={styles.header} className={cn({ alignRight: align === 'right' })}>
      <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>{title}</Typography>

      {sortable && (
        <button type="button" css={styles.sort} onClick={onToggleSort}>
          <Up
            size={ICON_SIZES.SMALL}
            fill={
              isSorted && sortDirection === SORT.DESC
                ? theme.colors.primary[500]
                : theme.colors.gray[300]
            }
          />
          <Down
            size={ICON_SIZES.SMALL}
            fill={
              isSorted && sortDirection === SORT.ASC
                ? theme.colors.primary[500]
                : theme.colors.gray[300]
            }
          />
        </button>
      )}
    </div>
  );
};

HeaderCellBase.displayName = 'HeaderCell';

export const HeaderCell = memo(HeaderCellBase);
